
import {Options, Vue} from 'vue-class-component';
import {DigitalSolutionsApi} from "@/api/DigitalSolutionsApi";
import {JcrCategoryNode} from "@/interfaces/jcr/JcrCategoryNode";
import {AxiosResponse} from "axios";
import {shallowRef} from "vue";

import Calendar from "@/assets/icons/icons-calendar-check.svg?inline";
import Wrench from "@/assets/icons/labels/wrench.svg?inline";
import Eye from "@/assets/icons/labels/eye.svg?inline";
import Pencil from "@/assets/icons/labels/pencil.svg?inline";
import ZoomIn from "@/assets/icons/labels/zoom-in.svg?inline";
import Loader from "@/components/Loader.vue";
import ServicePhasesCard from "@/components/ServicePhasesCard.vue";
import {DigitalSolution} from "@/models/DigitalSolution";
import {Prop} from "vue-property-decorator";

@Options({
  components: {
    ServicePhasesCard,
    Loader,
    Calendar,
    Wrench,
    Eye,
    Pencil,
    ZoomIn
  }
})
export default class ServicePhases extends Vue {

  private projectPhases: JcrCategoryNode[] = [];
  private areasOfExpertise: JcrCategoryNode[] = [];
  private digitalSolutions: DigitalSolution[] = [];
  private isLoading : boolean = false;
  private selectedAreaOfExpertise: string = "";

  private api = DigitalSolutionsApi.getInstance();

  private table: HTMLElement | null = null;
  private activePhase: string | null = null;

  @Prop({default: ''})
  private caption?: string | null;

  private readonly iconComponents: Object = {
    "calendar-check": shallowRef(Calendar),
    "wrench": shallowRef(Wrench),
    "eye": shallowRef(Eye),
    "pencil": shallowRef(Pencil),
    "zoom-in": shallowRef(ZoomIn)
  };

  private onAreaOfExpertiseChange() {
    this.isLoading = true;
    this.activePhase = this.projectPhases[0]["@name"];
    this.api.getDigitalSolutionsByAreaOfExpertise(this.selectedAreaOfExpertise).then((response: AxiosResponse) => {
      this.digitalSolutions = response.data.results;
    }).finally(() => {
      this.isLoading = false;
    })
  }

  /**
   * https://gist.github.com/andjosh/6764939
   * @param t = current time
   * @param b = start value
   * @param c = change in value
   * @param d = duration
   * @private
   */
  private static easeInOutQuad(t: number, b: number, c: number, d: number) {
    t /= d / 2;
    if (t < 1) {
      return c / 2 * t * t + b;
    }
    t--;
    return -c / 2 * (t * (t - 2) - 1) + b;
  }


  private moveTableTo(tabName: string) {
    if (!this.table) {
        return;
    }

    const table = this.$refs.table as HTMLElement;

    let currentTime = 0;
    const increment = 10;
    const toGoToTabRef: HTMLElement[] = this.$refs[`tab-${tabName}`] as HTMLElement[];
    const toGoToTab = toGoToTabRef[0] as HTMLElement;
    const tableWidth = table.offsetWidth;
    const currentScrollLeft = table.scrollLeft;

    if (toGoToTab) {
      const width = toGoToTab.offsetWidth;
      const offset = toGoToTab.offsetLeft;
      const newOffset = offset - ((tableWidth - width) / 2);
      const c = newOffset - currentScrollLeft;

      const animateScroll = () => {
        currentTime += increment;
        table.scrollLeft = ServicePhases.easeInOutQuad(currentTime, currentScrollLeft, c, 1250);
        if (currentTime < 1250) {
          setTimeout(animateScroll, increment);
        }
      };

      animateScroll();
      }
  }

  private onTableScroll() {
      if (!this.table) {
          return;
      }

      const maxScrollLeft = this.table.scrollWidth - this.table.clientWidth;
      const currentScrollLeft = this.table.scrollLeft;
      const buttons = this.$el.querySelectorAll('.service-phases__slide-button') as HTMLElement[];

      if (currentScrollLeft === maxScrollLeft) {
          this.activePhase = buttons[buttons.length-1].dataset.go || null;
      } else {

          for (const button of buttons) {
              const tabName = (button as HTMLElement).dataset.go;
              if (!tabName) {
                  return;
              }

              const toGoToTab: HTMLElement | null = this.table.querySelector(`th[data-name="${tabName}"]`);
              if (toGoToTab) {
                  const tableWidth = this.table.offsetWidth;
                  const width = toGoToTab.offsetWidth;
                  const offset = toGoToTab.offsetLeft;
                  const newOffset = offset - ((tableWidth - width) / 2);

                  if (currentScrollLeft >= newOffset - 1) {
                      this.activePhase = tabName;
                  }
              }
          }
      }

  }

  private showPopup(digitalSolutionId: string) {
    const refs = this.$refs;

    for (let ref in refs) {
      if(ref.toString().includes('popup')) {
        let tempPopup = this.$refs[ref] as HTMLElement[];

        if(tempPopup[0]) tempPopup[0].classList.remove('service-phases__popup--active');
      }
    }

    const popupRef = this.$refs[`popup-${digitalSolutionId}`] as HTMLElement[];
    const popup = popupRef[0];
    if(popup) popup.classList.add('service-phases__popup--active');
  }

  private closePopup(digitalSolutionId: string) {
    const popupRef = this.$refs[`popup-${digitalSolutionId}`] as HTMLElement[];
    const popup = popupRef[0];
    popup.classList.remove('service-phases__popup--active');
  }


  mounted() {
    this.table = this.$refs.table as HTMLElement;

    this.isLoading = true;
    this.api.getProjectPhases()
        .then((response: AxiosResponse) => {
          this.projectPhases = response.data.results;
        })
        .then(() => {
          this.api.getAreasOfExpertise().then((response: AxiosResponse) => {
            this.areasOfExpertise = response.data.results;
            this.selectedAreaOfExpertise = response.data.results[0]["@id"];
            this.onAreaOfExpertiseChange();
          })
        })
        .finally(() => {
          this.isLoading = false;
        })
  }
}
